import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Compass } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';

// Homepage-only quick tour (V1). Click-to-start, 4 tooltip steps anchored to the
// Guided Navigation cards. No localStorage, no routing, no backend — pure local
// state. Targets are resolved by the stable data-tour attributes added to
// GuidedNavCards. The tour is fully re-runnable and the trigger stays visible.
const STEPS = [
  { id: 'tour-events', title: 'Explore Events', body: 'Start here to explore events, featured nights, and upcoming experiences.' },
  { id: 'tour-djs', title: 'Discover DJs', body: 'Discover DJs, sounds, and artists shaping the SoundWolves scene.' },
  { id: 'tour-join', title: 'Login / Join', body: 'Sign in or join to manage events, tickets, and your profile.' },
  { id: 'tour-dashboard', title: 'Manage Dashboard', body: 'This is where attendees, DJs, organizers, and admins manage their next steps.' },
];

function prefersReducedMotion() {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function HomeTour() {
  const [open, setOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);   // { top, left, width, height } in viewport coords
  const triggerRef = useRef(null);

  const step = STEPS[stepIndex];
  const isLast = stepIndex === STEPS.length - 1;

  const close = useCallback(() => {
    setOpen(false);
    setStepIndex(0);
    setRect(null);
    // Return focus to the trigger for keyboard/screen-reader users.
    if (triggerRef.current) triggerRef.current.focus();
  }, []);

  const start = useCallback(() => {
    setStepIndex(0);
    setOpen(true);
  }, []);

  const next = useCallback(() => {
    if (isLast) { close(); return; }
    setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
  }, [isLast, close]);

  const back = useCallback(() => {
    setStepIndex((i) => Math.max(i - 1, 0));
  }, []);

  // On open / step change: scroll the target into view and keep the spotlight +
  // anchor glued to it every frame (covers smooth-scroll and layout shifts).
  useEffect(() => {
    if (!open) return undefined;
    const el = document.querySelector(`[data-tour="${step.id}"]`);
    if (!el) {
      // Defensive: if a target is missing, advance rather than crash/stall.
      if (isLast) close(); else setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
      return undefined;
    }

    el.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center', inline: 'nearest' });

    let raf = 0;
    const update = () => {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('resize', update);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', update);
    };
  }, [open, stepIndex, step.id, isLast, close]);

  return (
    <>
      <Button
        ref={triggerRef}
        type="button"
        variant="outline"
        size="lg"
        onClick={start}
        aria-haspopup="dialog"
        className="group"
      >
        <Compass className="w-5 h-5 mr-2" />
        Take a quick tour
      </Button>

      <Popover open={open} onOpenChange={(o) => { if (!o) close(); }}>
        {/* Blocker + spotlight are portaled to <body> so they escape the hero's
            z-10 stacking context and cover the whole page (including the fixed
            navbar). The transparent blocker captures all pointer events, so no
            underlying card/link/nav can be triggered while the tour is open —
            the user advances only via Next / Back / Skip / Finish (and Esc).
            Both unmount when the tour closes, restoring normal clicks. */}
        {open && createPortal(
          <>
            <div
              aria-hidden="true"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
              style={{ position: 'fixed', inset: 0, zIndex: 55, pointerEvents: 'auto' }}
            />
            {rect && (
              <div
                data-tour-spotlight=""
                aria-hidden="true"
                style={{
                  position: 'fixed',
                  top: rect.top - 6,
                  left: rect.left - 6,
                  width: rect.width + 12,
                  height: rect.height + 12,
                  borderRadius: '0.85rem',
                  pointerEvents: 'none',
                  zIndex: 60,
                  boxShadow: '0 0 0 9999px hsl(var(--background) / 0.72), 0 0 40px hsl(43 96% 56% / 0.35)',
                  outline: '2px solid hsl(43 96% 56% / 0.9)',
                  outlineOffset: '0px',
                }}
              />
            )}
          </>,
          document.body
        )}

        {open && rect && (
          <PopoverAnchor asChild>
            <div
              aria-hidden="true"
              style={{
                position: 'fixed',
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height,
                pointerEvents: 'none',
              }}
            />
          </PopoverAnchor>
        )}

        {open && rect && (
          <PopoverContent
            data-tour-popover=""
            key={stepIndex}
            side="bottom"
            align="center"
            sideOffset={16}
            collisionPadding={16}
            className="z-[70] w-80 max-w-[calc(100vw-2rem)]"
            onOpenAutoFocus={(e) => { /* keep default focus into content for a11y */ }}
            /* Outside clicks are absorbed by the blocker and must not close the
               tour — only Next/Back/Skip/Finish (and Esc) control it. */
            onPointerDownOutside={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
          >
            <div aria-live="polite">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-muted-foreground">
                  Step {stepIndex + 1} of {STEPS.length}
                </span>
                <button
                  type="button"
                  onClick={close}
                  aria-label="Skip tour"
                  className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  Skip
                </button>
              </div>

              <h3 className="font-display font-bold text-lg mb-1">
                <span className="text-gradient-gold">{step.title}</span>
              </h3>
              <p className="text-sm text-muted-foreground mb-4 leading-relaxed">{step.body}</p>

              {/* Progress dots */}
              <div className="flex items-center gap-1.5 mb-4" aria-hidden="true">
                {STEPS.map((s, i) => (
                  <span
                    key={s.id}
                    className={`h-1.5 rounded-full transition-all ${i === stepIndex ? 'w-5 bg-primary' : 'w-1.5 bg-muted'}`}
                  />
                ))}
              </div>

              <div className="flex items-center justify-between gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={back}
                  disabled={stepIndex === 0}
                >
                  Back
                </Button>
                <Button type="button" variant="premium" size="sm" onClick={next}>
                  {isLast ? 'Finish Tour' : 'Next'}
                </Button>
              </div>
            </div>
          </PopoverContent>
        )}
      </Popover>
    </>
  );
}

export default HomeTour;
